import {
  Alert,
  AlertTitle,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { createMultiplePaymentsTransactions } from 'src/shared/services/paymentService';
import { toast } from 'react-toastify';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';
import { Receipt } from '@mui/icons-material';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { InvoiceStatusEnum } from 'src/shared/models/enums/invoiceStatusEnum';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { PaymentsTransactionPost } from '../models/createTransaction';
import { MultiplePaymentDialogActions } from './multiplePaymentDialogActions';
import { InvoiceStatuses } from './invoiceStatuses';

type MultiplePaymentsCreateModalProps = {
  modalOpen: boolean;
  initialValues: PaymentsTransactionPost[];
  onSave: () => void;
  onClose: () => void;
};

type FormValues = {
  invoice: PaymentsTransactionPost[];
};
export const MultiplePaymentsCreateModal = (props: MultiplePaymentsCreateModalProps) => {
  const { modalOpen, initialValues, onSave, onClose } = props;
  const [transactionDate, setTransactionDate] = useState<Date | null>(null);
  const [paymentSource, setPaymentSource] = useState<string>('');
  const [validSubmit, setValidSubmit] = useState<boolean>(false);
  const isMounted = useIsMounted();

  const requiredMessage = t('common:validationMessages.required');

  useEffect(() => {
    const hasRelevantStatus = initialValues.some(
      (item) => item?.status?.id === InvoiceStatusEnum.UnPaid || item?.status?.id === InvoiceStatusEnum.Overdue,
    );
    setValidSubmit(hasRelevantStatus);
  }, [initialValues]);

  const PaymentsTransactionPostSchema: Schema<PaymentsTransactionPost> = yup.object().shape({
    ocr: yup
      .string()
      .min(5, t('common:validationMessages.minLength', { count: 5 }))
      .required(requiredMessage),
    amount: yup.number().min(1, requiredMessage).required(requiredMessage),
    sender: yup.string().optional(),
    comment: yup.string().optional(),
  });

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(
      yup.object().shape({
        invoice: yup.array().of(PaymentsTransactionPostSchema).required(),
      }),
    ),
    mode: 'all',
    defaultValues: { invoice: initialValues },
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
  } = formMethods;
  const { fields } = useFieldArray({
    control,
    name: 'invoice',
  });

  const onHandleSave = useCallback(() => {
    onSave();
  }, [onSave]);

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onSubmit = async (data) => {
    const dataToPost = data.invoice
      .filter((invoice) => invoice.status.id === InvoiceStatusEnum.UnPaid || invoice.status.id === InvoiceStatusEnum.Overdue)
      .map((invoice) => ({
        ocr: invoice.ocr,
        date: transactionDate ? DateHelper.postDate(transactionDate) : '',
        amount: invoice.amount,
        source: paymentSource,
        sender: 'FS',
        comment: '',
      }));

    try {
      await createMultiplePaymentsTransactions(dataToPost);
      if (isMounted()) {
        toast.success(t('invoice:transactionPage.createTransactionSuccess'));
        onHandleSave();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    }
  };

  return (
    <Dialog open={modalOpen} onClose={onHandleClose} maxWidth='md' fullWidth>
      <FormLoaderWrapper loading={isSubmitting}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle sx={{ m: 0, p: 2 }}>
              <Box sx={{ lineHeight: 1.2 }}>
                <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                  {t('invoice:transactionPage.createTransactionHeader')}
                </Typography>
              </Box>
              <DialogCloseButton handleClose={onHandleClose} />
            </DialogTitle>
            <Alert icon={false} severity='info' sx={{ m: 0 }}>
              {(fields || fields) && (
                <AlertTitle sx={{ display: 'flex', alignItems: 'center', paddingTop: 1, paddingBottom: 1 }}>
                  <Receipt fontSize='small' sx={{ mr: 1 }} aria-hidden />
                  {t('invoice:Invoices')}&nbsp;<strong>({fields && fields.length ? fields.length : 1})</strong>
                </AlertTitle>
              )}
            </Alert>
            <Divider />

            <DialogContent dividers sx={{ p: 2 }}>
              <TableContainer
                sx={{
                  mb: 2,
                  '& .head .MuiTableRow-head th': { borderBottom: '2px solid rgba(224, 224, 224, 1)' },
                  '& .footer .MuiTableRow-head th': { borderBottom: 'none' },
                  '& tbody td': { whiteSpace: 'nowrap' },
                }}
              >
                <Table size='small'>
                  <TableHead className='head'>
                    <TableRow>
                      <TableCell>{t('invoice:createInvoice.memberNr')}</TableCell>
                      <TableCell>{t('invoice:createInvoice.name')}</TableCell>
                      <TableCell>{t('invoice:ocr')}</TableCell>
                      <TableCell>{t('invoice:status')}</TableCell>
                      <TableCell>{t('invoice:createInvoice.dueDate')}</TableCell>

                      <TableCell align='right'>&nbsp;</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((item, index) => {
                      return (
                        <TableRow key={item.id}>
                          <TableCell width='30%'>{item.memberNr}</TableCell>
                          <TableCell width='50%'>{item.name}</TableCell>
                          <TableCell width='60%'>{item.ocr}</TableCell>
                          {item.status && (
                            <TableCell width='60%'>
                              <InvoiceStatuses status={item.status} chipSize='small' />
                            </TableCell>
                          )}
                          <TableCell width='50%'>{item.dueDate}</TableCell>
                          <TableCell align='right' size='small' sx={{ minWidth: 150 }}>
                            <Controller
                              name={`invoice.${index}.amount`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  type='number'
                                  label={t('invoice:amount')}
                                  value={field.value || ''}
                                  variant='outlined'
                                  size='small'
                                  fullWidth
                                  disabled={item.status?.id !== InvoiceStatusEnum.UnPaid && item.status?.id !== InvoiceStatusEnum.Overdue}
                                />
                              )}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions sx={{ p: 1 }}>
              <MultiplePaymentDialogActions
                errors={errors}
                transactionDate={transactionDate}
                setTransactionDate={setTransactionDate}
                paymentSource={paymentSource}
                setPaymentSource={setPaymentSource}
                isSubmitting={isSubmitting}
                onHandleClose={onHandleClose}
                validSubmit={validSubmit}
              />
            </DialogActions>
          </form>
        </FormProvider>
      </FormLoaderWrapper>
    </Dialog>
  );
};
